import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { EmailSendModal } from '@/shared/components/ui/EmailSendModal';
import { COMPANY_DOC_SLUGS } from '../constants';
import {
  ArrowLeft, CheckCircle, XCircle, Clock, FileText, Eye, ExternalLink, KeyRound, X, Download,
} from 'lucide-react';
import toast from 'react-hot-toast';

// ── Types ────────────────────────────────────────────────────────────────────

interface CandidateDoc {
  code: string; label: string; required: boolean; applicableForFreshers: boolean;
  status: string; fileUrl: string; submittedAt?: string; verifiedBy?: any; rejectionReason?: string;
}
interface CompanyDoc {
  code: string; label: string; status: string; fileUrl: string;
  generatedAt?: string; acknowledgedAt?: string;
}
interface Candidate {
  _id: string; candidateId: string; source: string;
  firstName: string; lastName: string; email: string; phone: string; alternatePhone: string;
  dateOfBirth: string; gender: string; maritalStatus: string; bloodGroup: string; disability: string;
  aadharNumber: string; panNumber: string; uanNumber: string; esicNumber: string; drivingLicense: string;
  currentAddress: { street: string; city: string; state: string; pincode: string };
  permanentAddress: { street: string; city: string; state: string; pincode: string };
  bankName: string; accountNumber: string; ifscCode: string; branchName: string;
  family: { name: string; relation: string; contact: string }[];
  education: { schoolName: string; degree: string; fieldOfStudy: string; completionDate: string }[];
  experience: { company: string; occupation: string; duration: string; reasonForLeaving: string }[];
  designation: string; department: string; dateOfJoining: string; location: string;
  highestQualification: string; currentSalary: number; reportingTo: string;
  employmentType: string; isFresher: boolean; skillSet: string[];
  onboardingStatus: string; completionPercent: number;
  candidateDocuments: CandidateDoc[]; companyDocuments: CompanyDoc[];
  eJoiningToken: string; addedBy?: any; addedAt: string; modifiedBy?: any; modifiedAt?: string;
  employeeUserId: { _id: string; username: string; active: number; display_name: string } | null;
}

// ── Hooks ────────────────────────────────────────────────────────────────────

function useCandidateDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['onboarding-candidates', 'detail', id],
    queryFn: async () => {
      const { data } = await api.get(`/api/hr-onboarding-candidates/candidates/${id}`);
      return data as Candidate;
    },
    enabled: !!id,
  });
}

function useVerifyDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, code }: { id: string; code: string }) => {
      const { data } = await api.post(`/api/hr-onboarding-candidates/candidates/${id}/verify-doc`, { code });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['onboarding-candidates'] }),
  });
}

function useRejectDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, code, reason }: { id: string; code: string; reason?: string }) => {
      const { data } = await api.post(`/api/hr-onboarding-candidates/candidates/${id}/reject-doc`, { code, reason });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['onboarding-candidates'] }),
  });
}

function useConvertToEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post(`/api/hr-onboarding-candidates/candidates/${id}/convert-to-employee`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['onboarding-candidates'] }),
  });
}

function useActivateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post(`/api/hr-onboarding-candidates/candidates/${id}/activate-account`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['onboarding-candidates'] }),
  });
}

// ── Status helpers ───────────────────────────────────────────────────────────

const statusColors: Record<string, string> = {
  pending_info: 'bg-gray-100 text-gray-700', info_submitted: 'bg-info/10 text-info-text',
  docs_pending: 'bg-warning/10 text-warning-text', docs_submitted: 'bg-indigo-100 text-indigo-800',
  verification: 'bg-purple-100 text-purple-800', completed: 'bg-success/10 text-success-text',
};

const sourceLabels: Record<string, string> = {
  recruitment: 'Recruitment', website: 'Website', linkedin: 'LinkedIn',
  naukri: 'Naukri', direct_hire: 'Direct Hire',
};

// ═══════════════════════════════════════════════════════════════════════════════
// FULL-SCREEN DETAIL PAGE
// ═══════════════════════════════════════════════════════════════════════════════

export function HrOnboardingDetailPage() {
  const { candidateId } = useParams<{ candidateId: string }>();
  const navigate = useNavigate();
  const { data: c, isLoading } = useCandidateDetail(candidateId);
  const verifyMut = useVerifyDoc();
  const rejectMut = useRejectDoc();
  const convertMut = useConvertToEmployee();
  const activateMut = useActivateAccount();
  const [emailDraft, setEmailDraft] = useState<{ to: string; subject: string; html: string } | null>(null);
  // Slide-over document viewer
  const [viewerDoc, setViewerDoc] = useState<{ code: string; label: string; status: string } | null>(null);

  if (isLoading || !c) {
    return (
      <div className="h-full p-6">
        <Skeleton className="h-[600px]" />
      </div>
    );
  }

  const docIcon = (s: string) => {
    if (s === 'verified') return <CheckCircle className="h-4 w-4 text-success-text" />;
    if (s === 'rejected') return <XCircle className="h-4 w-4 text-danger-text" />;
    if (s === 'submitted') return <FileText className="h-4 w-4 text-info-text" />;
    if (s === 'not_applicable') return <span className="text-[10px] text-gray-400">N/A</span>;
    return <Clock className="h-4 w-4 text-gray-400" />;
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Page Header */}
      <div className="px-6 py-4 border-b flex items-center justify-between bg-gray-50 shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-200 rounded-lg transition"
            title="Back to list"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="font-semibold text-lg text-gray-900">{c.firstName} {c.lastName}</h1>
            <p className="text-xs text-gray-500">
              {c.candidateId} — {c.designation || 'Position TBD'} —&nbsp;
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${statusColors[c.onboardingStatus]}`}>
                {c.onboardingStatus?.replace(/_/g, ' ')}
              </span>
            </p>
          </div>
        </div>
        {/* Completion progress */}
        <div className="flex items-center gap-2">
          <div className="w-24 h-1.5 rounded-full bg-gray-200 overflow-hidden">
            <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${c.completionPercent}%` }} />
          </div>
          <span className="text-sm font-medium text-gray-600">{c.completionPercent}%</span>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-auto p-6 bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 text-sm">

          {/* Section: Candidate Details */}
          <Section title="Candidate Details">
            <Grid>
              <KV label="Candidate ID" value={c.candidateId} />
              <KV label="First Name" value={c.firstName} />
              <KV label="Last Name" value={c.lastName} />
              <KV label="Email ID" value={c.email} />
              <KV label="Phone" value={c.phone} />
              <KV label="Alternate Phone" value={c.alternatePhone} />
              <KV label="Date of Birth" value={c.dateOfBirth?.slice(0, 10)} />
              <KV label="Gender" value={c.gender} />
              <KV label="Marital Status" value={c.maritalStatus} />
              <KV label="Blood Group" value={c.bloodGroup} />
              <KV label="Disability" value={c.disability} />
              <KV label="UAN Number" value={c.uanNumber} />
              <KV label="Aadhar Number" value={c.aadharNumber} />
              <KV label="PAN Number" value={c.panNumber} />
            </Grid>
          </Section>

          {/* Section: Address */}
          <Section title="Address Details">
            <Grid>
              <KV label="Current Address" value={fmtAddr(c.currentAddress)} span />
              <KV label="Permanent Address" value={fmtAddr(c.permanentAddress)} span />
            </Grid>
          </Section>

          {/* Section: Bank */}
          <Section title="Bank Details">
            <Grid>
              <KV label="Bank Name" value={c.bankName} />
              <KV label="Account Number" value={c.accountNumber} />
              <KV label="IFSC Code" value={c.ifscCode} />
              <KV label="Branch" value={c.branchName} />
            </Grid>
          </Section>

          {/* Section: Family */}
          <Section title="Family Details">
            {c.family?.length ? (
              <table className="w-full text-xs border-collapse">
                <thead><tr className="bg-muted/50">
                  <th className="px-2 py-1.5 text-left">S.No</th>
                  <th className="px-2 py-1.5 text-left">Name</th>
                  <th className="px-2 py-1.5 text-left">Relation</th>
                  <th className="px-2 py-1.5 text-left">Contact</th>
                </tr></thead>
                <tbody>{c.family.map((f, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-2 py-1.5">{i + 1}</td>
                    <td className="px-2 py-1.5">{f.name}</td>
                    <td className="px-2 py-1.5 capitalize">{f.relation}</td>
                    <td className="px-2 py-1.5">{f.contact}</td>
                  </tr>
                ))}</tbody>
              </table>
            ) : <p className="text-muted-foreground text-xs">No family details.</p>}
          </Section>

          {/* Section: Professional */}
          <Section title="Professional Details">
            <Grid>
              <KV label="Designation" value={c.designation} />
              <KV label="Department" value={c.department} />
              <KV label="Date of Joining" value={c.dateOfJoining?.slice(0, 10)} />
              <KV label="Location" value={c.location} />
              <KV label="Source of Hire" value={sourceLabels[c.source] || c.source} />
              <KV label="Skill Set" value={c.skillSet?.join(', ')} />
              <KV label="Highest Qualification" value={c.highestQualification} />
              <KV label="Current Salary" value={c.currentSalary ? `\u20B9${c.currentSalary.toLocaleString('en-IN')}` : '-'} />
              <KV label="Reporting To" value={c.reportingTo} />
              <KV label="Employment Type" value={c.employmentType} />
              <KV label="Fresher" value={c.isFresher ? 'Yes' : 'No'} />
            </Grid>
          </Section>

          {/* Section: Education */}
          <Section title="Education">
            {c.education?.length ? (
              <table className="w-full text-xs border-collapse">
                <thead><tr className="bg-muted/50">
                  <th className="px-2 py-1.5 text-left">School Name</th>
                  <th className="px-2 py-1.5 text-left">Degree</th>
                  <th className="px-2 py-1.5 text-left">Field</th>
                  <th className="px-2 py-1.5 text-left">Completion</th>
                </tr></thead>
                <tbody>{c.education.map((e, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-2 py-1.5">{e.schoolName || '-'}</td>
                    <td className="px-2 py-1.5">{e.degree || '-'}</td>
                    <td className="px-2 py-1.5">{e.fieldOfStudy || '-'}</td>
                    <td className="px-2 py-1.5">{e.completionDate?.slice(0, 10) || '-'}</td>
                  </tr>
                ))}</tbody>
              </table>
            ) : <p className="text-muted-foreground text-xs">No education records.</p>}
          </Section>

          {/* Section: Experience */}
          <Section title="Work Experience">
            {c.isFresher
              ? <p className="text-muted-foreground text-xs italic">Fresher — no previous experience.</p>
              : c.experience?.length ? (
                <table className="w-full text-xs border-collapse">
                  <thead><tr className="bg-muted/50">
                    <th className="px-2 py-1.5 text-left">Company</th>
                    <th className="px-2 py-1.5 text-left">Role</th>
                    <th className="px-2 py-1.5 text-left">Duration</th>
                    <th className="px-2 py-1.5 text-left">Reason</th>
                  </tr></thead>
                  <tbody>{c.experience.map((e, i) => (
                    <tr key={i} className="border-t">
                      <td className="px-2 py-1.5">{e.company || '-'}</td>
                      <td className="px-2 py-1.5">{e.occupation || '-'}</td>
                      <td className="px-2 py-1.5">{e.duration || '-'}</td>
                      <td className="px-2 py-1.5">{e.reasonForLeaving || '-'}</td>
                    </tr>
                  ))}</tbody>
                </table>
              ) : <p className="text-muted-foreground text-xs">No experience records.</p>}
          </Section>

          {/* Section: Company Documents — full width */}
          <div className="lg:col-span-2">
            <Section title="Company Documents">
              <div className="space-y-1.5">
                {c.companyDocuments?.map(doc => {
                  const issued = doc.status === 'issued' || doc.status === 'acknowledged';
                  const draft = doc.status === 'draft';
                  return (
                    <div key={doc.code} className="flex items-center justify-between p-2 border rounded">
                      <div className="flex items-center gap-2">
                        {issued
                          ? <CheckCircle className="h-3.5 w-3.5 text-success-text" />
                          : draft
                          ? <FileText className="h-3.5 w-3.5 text-warning-text" />
                          : <Clock className="h-3.5 w-3.5 text-gray-400" />}
                        <span className="text-xs">{doc.label}</span>
                        {issued && <span className="text-[10px] text-success-text font-medium">Issued</span>}
                        {draft && <span className="text-[10px] text-warning-text font-medium">Draft</span>}
                      </div>
                      <div className="flex gap-1">
                        {COMPANY_DOC_SLUGS[doc.code] ? (
                          <button
                            onClick={() => navigate(`/hr-onboarding/${c._id}/documents/${doc.code}`)}
                            className="text-[10px] px-2 py-1 bg-gray-100 rounded hover:bg-gray-200 flex items-center gap-1"
                          >
                            <Eye className="h-3 w-3" />
                            {doc.status === 'not_generated' ? 'Open' : 'View / Edit'}
                          </button>
                        ) : (
                          <span className="text-[10px] text-gray-400">No template</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Section>
          </div>

          {/* Section: Candidate Documents — full width */}
          <div className="lg:col-span-2">
            <Section title="Candidate Documents">
              <div className="space-y-1.5">
                {c.candidateDocuments?.map(doc => {
                  const hasFile = ['submitted', 'verified', 'rejected'].includes(doc.status);
                  return (
                    <div key={doc.code} className="flex items-center justify-between p-2 border rounded hover:bg-gray-50 transition">
                      <div className="flex items-center gap-2">
                        {docIcon(doc.status)}
                        <div>
                          <span className="text-xs">{doc.label}</span>
                          {doc.required && doc.status !== 'not_applicable' && (
                            <span className="text-danger-text text-[10px] ml-1">*</span>
                          )}
                          {doc.status === 'rejected' && (
                            <span className="text-[10px] text-danger-text ml-2">({doc.rejectionReason})</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {/* View button — shown whenever a file has been uploaded */}
                        {hasFile && (
                          <button
                            onClick={() => setViewerDoc({ code: doc.code, label: doc.label, status: doc.status })}
                            className="text-[10px] px-2 py-1 bg-indigo-50 text-indigo-700 rounded hover:bg-indigo-100 flex items-center gap-1"
                          >
                            <Eye className="h-3 w-3" /> View
                          </button>
                        )}
                        {doc.status === 'submitted' && (
                          <>
                            <button
                              onClick={() =>
                                verifyMut.mutateAsync({ id: c._id, code: doc.code })
                                  .then(() => toast.success('Verified.'))
                              }
                              className="text-[10px] px-2 py-1 bg-success/10 text-success-text rounded hover:bg-success/10"
                            >
                              Verify
                            </button>
                            <button
                              onClick={() => {
                                const r = prompt('Rejection reason:');
                                if (r) rejectMut.mutateAsync({ id: c._id, code: doc.code, reason: r })
                                  .then(() => toast.success('Rejected.'));
                              }}
                              className="text-[10px] px-2 py-1 bg-danger/10 text-danger-text rounded hover:bg-danger/10"
                            >
                              Reject
                            </button>
                          </>
                        )}
                        {doc.status === 'verified' && <span className="text-[10px] text-success-text font-medium">Verified ✓</span>}
                        {doc.status === 'not_applicable' && <span className="text-[10px] text-gray-400 italic">N/A</span>}
                        {doc.status === 'pending' && <span className="text-[10px] text-warning-text">Awaiting upload</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Section>
          </div>

          {/* Section: Employee Account — full width */}
          <div className="lg:col-span-2">
            <Section title="Employee Account">
              {!c.employeeUserId ? (
                <div className="flex items-center justify-between gap-3 p-2">
                  <p className="text-xs text-muted-foreground">No Zynx account has been created for this candidate yet.</p>
                  <button
                    onClick={() =>
                      convertMut.mutateAsync(c._id).then((r: any) =>
                        toast.success(r?.account ? `Account created: ${r.account.username} (inactive)` : 'Converted to employee.')
                      )
                    }
                    disabled={convertMut.isPending}
                    className="text-[10px] px-2 py-1 bg-indigo-50 text-indigo-700 rounded hover:bg-indigo-100 whitespace-nowrap"
                  >
                    {convertMut.isPending ? 'Creating...' : 'Convert to Employee'}
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-3 p-2">
                  <div className="text-xs">
                    <span className="font-medium">{c.employeeUserId.username}</span>
                    {c.employeeUserId.active ? (
                      <span className="ml-2 text-[10px] text-success-text font-medium">Active</span>
                    ) : (
                      <span className="ml-2 text-[10px] text-warning-text font-medium">Inactive — cannot log in yet</span>
                    )}
                  </div>
                  {!c.employeeUserId.active && (
                    <button
                      onClick={() =>
                        activateMut.mutateAsync(c._id).then((r: any) => {
                          toast.success('Account activated.');
                          if (r?.emailDraft) setEmailDraft(r.emailDraft);
                        })
                      }
                      disabled={activateMut.isPending}
                      className="text-[10px] px-2 py-1 bg-success/10 text-success-text rounded hover:bg-success/10 flex items-center gap-1 whitespace-nowrap"
                    >
                      <KeyRound className="h-3 w-3" />
                      {activateMut.isPending ? 'Activating...' : 'Activate Account'}
                    </button>
                  )}
                </div>
              )}
            </Section>
          </div>

          {/* Section: Onboarding Meta */}
          <Section title="Onboarding Info">
            <Grid>
              <KV label="Status" value={c.onboardingStatus?.replace(/_/g, ' ')} />
              <KV label="Completion" value={`${c.completionPercent}%`} />
              <KV label="Added By" value={c.addedBy?.display_name || '-'} />
              <KV label="Added Time" value={c.addedAt ? new Date(c.addedAt).toLocaleString('en-IN') : '-'} />
              <KV label="Modified By" value={c.modifiedBy?.display_name || '-'} />
              <KV label="Modified Time" value={c.modifiedAt ? new Date(c.modifiedAt).toLocaleString('en-IN') : '-'} />
              <KV label="E-Joining Link" value={c.eJoiningToken ? `/e-joining/${c.eJoiningToken}` : '-'} span link />
            </Grid>
          </Section>

        </div>
      </div>

      {/* Activation email draft */}
      {emailDraft && (
        <EmailSendModal
          open={!!emailDraft}
          onClose={() => setEmailDraft(null)}
          initialTo={emailDraft.to}
          initialSubject={emailDraft.subject}
          initialHtml={emailDraft.html}
          onSent={() => setEmailDraft(null)}
        />
      )}

      {/* ── Document Viewer Slide-over ───────────────────────────────────── */}
      {viewerDoc && (
        <CandidateDocViewer
          candidateId={c._id}
          doc={viewerDoc}
          onClose={() => setViewerDoc(null)}
          onVerify={() =>
            verifyMut.mutateAsync({ id: c._id, code: viewerDoc.code })
              .then(() => { toast.success('Verified.'); setViewerDoc(null); })
          }
          onReject={(reason) =>
            rejectMut.mutateAsync({ id: c._id, code: viewerDoc.code, reason })
              .then(() => { toast.success('Rejected.'); setViewerDoc(null); })
          }
          verifyPending={verifyMut.isPending}
          rejectPending={rejectMut.isPending}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CANDIDATE DOCUMENT VIEWER — slide-over panel
// ═══════════════════════════════════════════════════════════════════════════════

interface DocViewerProps {
  candidateId: string;
  doc: { code: string; label: string; status: string };
  onClose: () => void;
  onVerify: () => void;
  onReject: (reason: string) => void;
  verifyPending: boolean;
  rejectPending: boolean;
}

function CandidateDocViewer({ candidateId, doc, onClose, onVerify, onReject, verifyPending, rejectPending }: DocViewerProps) {
  const [rejectReason, setRejectReason] = React.useState('');
  const [showRejectInput, setShowRejectInput] = React.useState(false);
  const [blobUrl, setBlobUrl] = React.useState<string | null>(null);
  const [mimeType, setMimeType] = React.useState<string>('');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setBlobUrl(null);

    // Use native fetch so we don't send Content-Type:application/json on a
    // binary download request (the axios instance does this globally). Auth
    // cookies are sent automatically via credentials:'include'.
    fetch(`/api/hr-onboarding-candidates/candidates/${candidateId}/candidate-documents/${doc.code}/file`, {
      credentials: 'include',
    })
      .then(async (res) => {
        if (!res.ok) {
          // Try to parse the JSON error body the backend sends
          let msg = `Server error ${res.status}`;
          try { const j = await res.json(); msg = j?.error || msg; } catch { /* ignore */ }
          throw new Error(msg);
        }
        const mime = res.headers.get('content-type') || 'application/octet-stream';
        const blob = await res.blob();
        // Re-create blob with explicit MIME so createObjectURL sets it correctly
        const typedBlob = new Blob([blob], { type: mime });
        objectUrl = URL.createObjectURL(typedBlob);
        if (!cancelled) {
          setMimeType(mime);
          setBlobUrl(objectUrl);
          setLoading(false);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setError(err.message || 'Failed to load document.');
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [candidateId, doc.code]);

  const handleReject = () => {
    if (!rejectReason.trim()) return;
    onReject(rejectReason.trim());
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[9998] bg-black/40"
        onClick={onClose}
      />
      {/* Slide-over panel */}
      <div className="fixed right-0 top-0 bottom-0 z-[9999] w-[520px] max-w-[95vw] bg-white shadow-2xl flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b flex items-center justify-between bg-gray-50 shrink-0">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Candidate Document</p>
            <h2 className="font-semibold text-gray-900 text-sm mt-0.5">{doc.label}</h2>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-200 rounded-lg transition">
            <X className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        {/* Document preview area */}
        <div className="flex-1 overflow-hidden bg-gray-100 flex flex-col">
          {loading && (
            <div className="flex-1 flex items-center justify-center">
              <div className="flex flex-col items-center gap-2">
                <div className="h-6 w-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                <span className="text-sm text-gray-500">Loading document...</span>
              </div>
            </div>
          )}
          {error && (
            <div className="flex-1 flex items-center justify-center px-6">
              <div className="text-sm text-danger-text text-center">
                <XCircle className="h-10 w-10 mx-auto mb-3 text-danger-text" />
                <p className="font-medium">Could not load document</p>
                <p className="text-xs text-danger-text mt-1">{error}</p>
              </div>
            </div>
          )}
          {!loading && !error && blobUrl && (
            <>
              {/* Images */}
              {mimeType.startsWith('image/') && (
                <div className="flex-1 overflow-auto flex items-center justify-center p-4">
                  <img
                    src={blobUrl}
                    alt={doc.label}
                    className="max-w-full max-h-full object-contain rounded shadow"
                  />
                </div>
              )}
              {/* PDFs — iframe with blob URL (works when Blob has correct MIME) */}
              {mimeType === 'application/pdf' && (
                <iframe
                  src={blobUrl}
                  title={doc.label}
                  className="flex-1 w-full border-0"
                  style={{ minHeight: 0 }}
                />
              )}
              {/* Office / unknown — can't render inline, show download option */}
              {!mimeType.startsWith('image/') && mimeType !== 'application/pdf' && (
                <div className="flex-1 flex flex-col items-center justify-center gap-3 p-6 text-center">
                  <FileText className="h-12 w-12 text-gray-300" />
                  <p className="text-sm text-gray-700 font-medium">
                    {mimeType.includes('word') ? 'Word Document (.docx)' :
                     mimeType.includes('sheet') ? 'Excel Spreadsheet (.xlsx)' :
                     'Document uploaded'}
                  </p>
                  <p className="text-xs text-gray-400">This file format cannot be previewed in the browser.</p>
                  <a
                    href={blobUrl}
                    download={`${doc.code}-document`}
                    className="mt-1 text-sm px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" /> Download to view
                  </a>
                </div>
              )}
            </>
          )}
        </div>


        {/* Action footer */}
        <div className="px-5 py-4 border-t bg-white shrink-0 space-y-3">
          {/* Download link */}
          {blobUrl && (
            <a
              href={blobUrl}
              download={`${doc.code}-document`}
              className="flex items-center gap-1.5 text-xs text-indigo-600 hover:underline w-fit"
            >
              <Download className="h-3.5 w-3.5" /> Download file
            </a>
          )}

          {doc.status === 'submitted' && !showRejectInput && (
            <div className="flex gap-2">
              <button
                onClick={onVerify}
                disabled={verifyPending}
                className="flex-1 py-2 bg-success hover:bg-success disabled:opacity-60 text-white text-sm font-medium rounded-lg transition flex items-center justify-center gap-1.5"
              >
                <CheckCircle className="h-4 w-4" />
                {verifyPending ? 'Verifying...' : 'Verify Document'}
              </button>
              <button
                onClick={() => setShowRejectInput(true)}
                className="flex-1 py-2 bg-danger/10 hover:bg-danger/10 text-danger-text text-sm font-medium rounded-lg border border-danger/25 transition flex items-center justify-center gap-1.5"
              >
                <XCircle className="h-4 w-4" />
                Reject
              </button>
            </div>
          )}

          {doc.status === 'submitted' && showRejectInput && (
            <div className="space-y-2">
              <textarea
                className="w-full border border-danger/25 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-danger/40"
                rows={2}
                placeholder="Enter rejection reason..."
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                autoFocus
              />
              <div className="flex gap-2">
                <button
                  onClick={handleReject}
                  disabled={!rejectReason.trim() || rejectPending}
                  className="flex-1 py-2 bg-danger hover:bg-danger disabled:opacity-50 text-white text-sm font-medium rounded-lg transition"
                >
                  {rejectPending ? 'Rejecting...' : 'Confirm Reject'}
                </button>
                <button
                  onClick={() => { setShowRejectInput(false); setRejectReason(''); }}
                  className="py-2 px-4 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {doc.status === 'verified' && (
            <div className="flex items-center gap-2 text-success-text bg-success/10 rounded-lg px-3 py-2">
              <CheckCircle className="h-4 w-4" />
              <span className="text-sm font-medium">This document has been verified.</span>
            </div>
          )}

          {doc.status === 'rejected' && (
            <div className="flex items-center gap-2 text-danger-text bg-danger/10 rounded-lg px-3 py-2">
              <XCircle className="h-4 w-4" />
              <span className="text-sm font-medium">This document was rejected.</span>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// UTILITY COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 pb-1 border-b">{title}</h3>
      {children}
    </div>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">{children}</div>;
}

function KV({ label, value, span, link }: { label: string; value?: string; span?: boolean; link?: boolean }) {
  return (
    <div className={span ? 'col-span-2' : ''}>
      <span className="text-[11px] text-muted-foreground">{label}</span>
      {link && value && value !== '-' ? (
        <a href={value} target="_blank" rel="noopener noreferrer" className="block text-xs font-medium text-primary hover:underline flex items-center gap-1">
          {value}<ExternalLink className="h-3 w-3" />
        </a>
      ) : (
        <p className="text-xs font-medium capitalize">{value || '-'}</p>
      )}
    </div>
  );
}

function fmtAddr(addr?: { street?: string; city?: string; state?: string; pincode?: string }) {
  if (!addr) return '-';
  return [addr.street, addr.city, addr.state, addr.pincode].filter(Boolean).join(', ') || '-';
}
